import { ChevronDown, Loader2, Search, Trash2, UserPlus, Users, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { getRoleConfig, PROJECT_ROLE_OPTIONS } from "@/components/project-hub/utils";
import { useConfirm, useToast } from "@/components/shared";
import { authService, projectService } from "@/lib/services";
import type { ProjectHub as ProjectHubType, ProjectMember, UserSearchResult } from "@/types";

// ---------------------------------------------------------------------------
// Members Tab
// ---------------------------------------------------------------------------

export function MembersTab({
  hub,
  isOwner,
  canEdit,
  projectId,
  onRefresh,
}: Readonly<{
  hub: ProjectHubType;
  isOwner: boolean;
  canEdit: boolean;
  projectId: string;
  onRefresh: () => Promise<void>;
}>) {
  const { showToast } = useToast();
  const { confirm } = useConfirm();

  // Add member state
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [addMemberForm, setAddMemberForm] = useState({ email: "", role: "editor" });
  const [addingMember, setAddingMember] = useState(false);

  // User search state
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [userSearchResults, setUserSearchResults] = useState<UserSearchResult[]>([]);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Role dropdown state
  const [roleDropdownOpen, setRoleDropdownOpen] = useState<string | null>(null);
  const [updatingRole, setUpdatingRole] = useState<string | null>(null);

  // Debounced user search
  useEffect(() => {
    if (!userSearchQuery.trim() || userSearchQuery.length < 2) {
      setUserSearchResults([]);
      setShowUserDropdown(false);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setIsSearchingUsers(true);
        const response = await authService.searchUsers(userSearchQuery);
        const existingMemberEmails = new Set(hub.members.map((m) => m.email.toLowerCase()));
        const filtered = response.data.filter((u) => !existingMemberEmails.has(u.email.toLowerCase()));
        setUserSearchResults(filtered);
        setShowUserDropdown(true);
      } catch (err) {
        console.error("Failed to search users:", err);
      } finally {
        setIsSearchingUsers(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [userSearchQuery, hub.members]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setShowUserDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectUser = (user: UserSearchResult) => {
    setSelectedUser(user);
    setAddMemberForm({ ...addMemberForm, email: user.email });
    setUserSearchQuery(user.name || user.email);
    setShowUserDropdown(false);
    setUserSearchResults([]);
  };

  const clearSelectedUser = () => {
    setSelectedUser(null);
    setAddMemberForm({ ...addMemberForm, email: "" });
    setUserSearchQuery("");
    searchInputRef.current?.focus();
  };

  const resetAddMemberState = () => {
    setShowAddMemberModal(false);
    setAddMemberForm({ email: "", role: "editor" });
    setUserSearchQuery("");
    setUserSearchResults([]);
    setSelectedUser(null);
    setShowUserDropdown(false);
  };

  const handleAddMember = async () => {
    if (!addMemberForm.email.trim()) return;
    try {
      setAddingMember(true);
      await projectService.addMember(projectId, {
        email: addMemberForm.email.trim(),
        role: addMemberForm.role as "owner" | "editor" | "viewer",
      });
      showToast("Member added successfully", "success");
      resetAddMemberState();
      await onRefresh();
    } catch (err: unknown) {
      console.error("Failed to add member:", err);
      const apiErr = err as { data?: { detail?: string } };
      const detail = apiErr?.data?.detail;
      showToast(detail || "Failed to add member", "error");
    } finally {
      setAddingMember(false);
    }
  };

  const handleUpdateRole = async (member: ProjectMember, newRole: string) => {
    try {
      setUpdatingRole(member.userUuid);
      setRoleDropdownOpen(null);
      await projectService.updateMember(projectId, member.userUuid, newRole);
      showToast(`Role updated to ${getRoleConfig(newRole).label}`, "success");
      await onRefresh();
    } catch (err: unknown) {
      console.error("Failed to update role:", err);
      const apiErr = err as { data?: { detail?: string } };
      const detail = apiErr?.data?.detail;
      showToast(detail || "Failed to update role", "error");
    } finally {
      setUpdatingRole(null);
    }
  };

  const handleRemoveMember = async (member: ProjectMember) => {
    setRoleDropdownOpen(null);
    const confirmed = await confirm({
      title: "Remove Member",
      message: `Are you sure you want to remove ${member.name || member.email} from this project?`,
      confirmText: "Remove",
      variant: "danger",
    });
    if (!confirmed) return;

    try {
      await projectService.removeMember(projectId, member.userUuid);
      showToast("Member removed successfully", "success");
      await onRefresh();
    } catch (err: unknown) {
      console.error("Failed to remove member:", err);
      const apiErr = err as { data?: { detail?: string } };
      const detail = apiErr?.data?.detail;
      showToast(detail || "Failed to remove member", "error");
    }
  };

  return (
    <div className="p-6">
      {/* Header with Add Members button */}
      {canEdit && (
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-content-primary">Members ({hub.members.length})</h2>
          <button
            onClick={() => setShowAddMemberModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
          >
            <UserPlus className="h-4 w-4" />
            Add Members
          </button>
        </div>
      )}

      {hub.members.length > 0 ? (
        <div className="bg-surface-primary border border-border-primary rounded-lg overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border-primary bg-surface-primary/50">
                <th className="text-left px-6 py-3 text-xs font-medium text-content-tertiary uppercase tracking-wider">
                  Member
                </th>
                <th className="text-left px-6 py-3 text-xs font-medium text-content-tertiary uppercase tracking-wider">
                  Role
                </th>
                <th className="text-left px-6 py-3 text-xs font-medium text-content-tertiary uppercase tracking-wider">
                  Joined
                </th>
                {isOwner && (
                  <th className="text-right px-6 py-3 text-xs font-medium text-content-tertiary uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-border-primary">
              {hub.members.map((member) => {
                const roleConfig = getRoleConfig(member.role);
                const RoleIcon = roleConfig.icon;
                const isUpdating = updatingRole === member.userUuid;

                return (
                  <tr key={member.userUuid} className="hover:bg-interactive-hover transition-colors group">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-linear-to-br from-brand-primary/30 to-brand-secondary/30 flex items-center justify-center shrink-0">
                          <span className="text-sm font-medium text-content-primary">
                            {(member.name || member.email).charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-content-primary truncate">
                            {member.name || "Unnamed User"}
                          </p>
                          <p className="text-xs text-content-tertiary truncate">{member.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {isUpdating && <Loader2 className="h-4 w-4 animate-spin text-content-tertiary" />}
                      {!isUpdating && isOwner && (
                        <div className="relative">
                          <button
                            onClick={() =>
                              setRoleDropdownOpen(roleDropdownOpen === member.userUuid ? null : member.userUuid)
                            }
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md ${roleConfig.bg} hover:opacity-80 transition-opacity`}
                          >
                            <RoleIcon className={`h-3 w-3 ${roleConfig.color}`} />
                            <span className={`text-xs font-medium ${roleConfig.color}`}>{roleConfig.label}</span>
                            <ChevronDown className={`h-3 w-3 ${roleConfig.color}`} />
                          </button>

                          {/* Role dropdown */}
                          {roleDropdownOpen === member.userUuid && (
                            <>
                              <button
                                type="button"
                                className="fixed inset-0 z-30 cursor-default"
                                aria-label="Close dropdown"
                                onClick={() => setRoleDropdownOpen(null)}
                                onKeyDown={(e) => {
                                  if (e.key === "Escape") setRoleDropdownOpen(null);
                                }}
                              />
                              <div className="absolute top-full left-0 mt-1 bg-surface-primary border border-border-primary rounded-lg shadow-xl py-1 min-w-[200px] z-40">
                                {PROJECT_ROLE_OPTIONS.map((option) => {
                                  const OptionIcon = option.icon;
                                  const isActive = member.role === option.value;
                                  return (
                                    <button
                                      key={option.value}
                                      onClick={() => {
                                        if (isActive) {
                                          setRoleDropdownOpen(null);
                                        } else {
                                          handleUpdateRole(member, option.value);
                                        }
                                      }}
                                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                                        isActive
                                          ? "bg-interactive-hover text-content-primary"
                                          : "text-content-secondary hover:bg-interactive-hover"
                                      }`}
                                    >
                                      <OptionIcon className="h-4 w-4" />
                                      <div className="text-left">
                                        <p className="font-medium">{option.label}</p>
                                        <p className="text-xs text-content-quaternary">{option.description}</p>
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                      {!isUpdating && !isOwner && (
                        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md ${roleConfig.bg}`}>
                          <RoleIcon className={`h-3 w-3 ${roleConfig.color}`} />
                          <span className={`text-xs font-medium ${roleConfig.color}`}>{roleConfig.label}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-content-tertiary">
                      {new Date(member.joinedAt).toLocaleDateString()}
                    </td>
                    {isOwner && (
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end">
                          <button
                            onClick={() => handleRemoveMember(member)}
                            className="p-2 text-content-tertiary hover:text-red-400 hover:bg-interactive-active rounded-sm transition-colors opacity-0 group-hover:opacity-100"
                            title="Remove member"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="h-16 w-16 rounded-2xl bg-linear-to-br from-brand-primary/20 to-brand-secondary/20 flex items-center justify-center mb-4">
            <Users className="h-8 w-8 text-brand-primary" />
          </div>
          <h3 className="text-lg font-medium text-content-primary">No members yet</h3>
          <p className="text-content-tertiary mt-1 text-center max-w-sm">
            Add members to this project to start collaborating.
          </p>
          {canEdit && (
            <button
              onClick={() => setShowAddMemberModal(true)}
              className="mt-6 flex items-center gap-2 px-4 py-2.5 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
            >
              <UserPlus className="h-4 w-4" />
              Add Members
            </button>
          )}
        </div>
      )}

      {/* Add Member Modal */}
      {showAddMemberModal && (
        <div className="fixed inset-0 bg-surface-overlay backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-surface-primary border border-border-primary rounded-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b border-border-primary">
              <h2 className="text-lg font-semibold text-content-primary">Add Members</h2>
              <button
                onClick={resetAddMemberState}
                disabled={addingMember}
                className="p-1 text-content-tertiary hover:text-content-primary transition-colors disabled:opacity-50"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label
                  htmlFor="project-search-user-input"
                  className="block text-sm font-medium text-content-secondary mb-1.5"
                >
                  Search User <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  {selectedUser ? (
                    <div className="flex items-center gap-3 w-full px-3 py-2 bg-surface-secondary border border-brand-primary/50 rounded-lg">
                      <div className="h-8 w-8 rounded-full bg-brand-primary/20 flex items-center justify-center shrink-0">
                        <span className="text-sm font-medium text-brand-primary">
                          {(selectedUser.name || selectedUser.email).charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-content-primary truncate">{selectedUser.name}</p>
                        <p className="text-xs text-content-tertiary truncate">{selectedUser.email}</p>
                      </div>
                      <button
                        onClick={clearSelectedUser}
                        disabled={addingMember}
                        className="p-1 text-content-tertiary hover:text-content-primary transition-colors disabled:opacity-50"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-content-quaternary" />
                        <input
                          id="project-search-user-input"
                          ref={searchInputRef}
                          type="text"
                          placeholder="Search by name or email..."
                          value={userSearchQuery}
                          onChange={(e) => {
                            setUserSearchQuery(e.target.value);
                            setAddMemberForm({ ...addMemberForm, email: e.target.value });
                          }}
                          onFocus={() => {
                            if (userSearchResults.length > 0) setShowUserDropdown(true);
                          }}
                          className="w-full pl-9 pr-9 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
                        />
                        {isSearchingUsers && (
                          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-content-quaternary animate-spin" />
                        )}
                      </div>
                      {showUserDropdown && userSearchResults.length > 0 && (
                        <div
                          ref={dropdownRef}
                          className="absolute left-0 right-0 top-full mt-1 bg-surface-primary border border-border-primary rounded-lg shadow-lg overflow-hidden z-10 max-h-48 overflow-y-auto"
                        >
                          {userSearchResults.map((result) => (
                            <button
                              key={result.uuid}
                              onClick={() => handleSelectUser(result)}
                              className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-surface-hover transition-colors text-left"
                            >
                              <div className="h-8 w-8 rounded-full bg-brand-primary/20 flex items-center justify-center shrink-0">
                                <span className="text-sm font-medium text-brand-primary">
                                  {(result.name || result.email).charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-content-primary truncate">{result.name}</p>
                                <p className="text-xs text-content-tertiary truncate">{result.email}</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                      {showUserDropdown &&
                        userSearchQuery.length >= 2 &&
                        !isSearchingUsers &&
                        userSearchResults.length === 0 && (
                          <div className="absolute left-0 right-0 top-full mt-1 bg-surface-primary border border-border-primary rounded-lg shadow-lg p-3 z-10">
                            <p className="text-sm text-content-tertiary text-center">
                              No users found matching &quot;{userSearchQuery}&quot;
                            </p>
                          </div>
                        )}
                    </>
                  )}
                </div>
                <p className="mt-1 text-xs text-content-quaternary">
                  Type at least 2 characters to search for platform users.
                </p>
              </div>
              <div>
                <span className="block text-sm font-medium text-content-secondary mb-1.5">Role</span>
                <div className="space-y-2">
                  {PROJECT_ROLE_OPTIONS.map((option) => {
                    const OptionIcon = option.icon;
                    const isSelected = addMemberForm.role === option.value;
                    return (
                      <button
                        key={option.value}
                        onClick={() => setAddMemberForm({ ...addMemberForm, role: option.value })}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border transition-colors text-left ${
                          isSelected
                            ? "border-brand-primary bg-brand-primary/10"
                            : "border-border-secondary hover:border-border-hover bg-surface-secondary"
                        }`}
                      >
                        <OptionIcon
                          className={`h-4 w-4 ${isSelected ? "text-brand-primary" : "text-content-tertiary"}`}
                        />
                        <div>
                          <p
                            className={`text-sm font-medium ${isSelected ? "text-brand-primary" : "text-content-primary"}`}
                          >
                            {option.label}
                          </p>
                          <p className="text-xs text-content-quaternary">{option.description}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-4 border-t border-border-primary">
              <button
                onClick={resetAddMemberState}
                disabled={addingMember}
                className="px-4 py-2 text-content-secondary hover:text-content-primary transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddMember}
                disabled={addingMember || !addMemberForm.email.trim()}
                className="px-4 py-2 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addingMember ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                    Adding...
                  </>
                ) : (
                  "Add Members"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
